import { useEffect, useState } from "react";
import { TopNav } from "../components/TopNav";
import { Footer } from "../components/Footer";
import styles from "./LegalPage.module.css";

const LEGAL_BASE = "https://assets.atlasdesk.co/legal";

/**
 * Shared renderer for the /terms and /privacy routes. Both pages
 * follow the same shape — title, intro paragraph, then a list of
 * (heading, body) sections — so a single component swapped on
 * `kind` is enough. Content lives on R2 (uploaded via
 * `pnpm legal:publish`) and is fetched at page load so the legal
 * text can be revised without re-deploying the site.
 *
 * Versioned snapshots (`<date>.json`) sit alongside `latest.json`
 * for legal record-keeping but aren't surfaced in this UI — readers
 * always see the current version.
 */

interface LegalSection {
  heading: string;
  body: string;
}

interface LegalDoc {
  kind: "terms" | "privacy";
  version: string;
  effective: string;
  title: string;
  intro: string;
  sections: LegalSection[];
}

interface LegalPageProps {
  kind: "terms" | "privacy";
  /** Page title used in browser tab + main heading fallback while
   *  the JSON is still loading. */
  fallbackTitle: string;
}

type Fetch =
  | { kind: "loading" }
  | { kind: "ready"; doc: LegalDoc }
  | { kind: "error"; message: string };

export function LegalPage({ kind, fallbackTitle }: LegalPageProps) {
  const [state, setState] = useState<Fetch>({ kind: "loading" });

  useEffect(() => {
    document.title = `${fallbackTitle} — Atlas Desk`;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${LEGAL_BASE}/${kind}/latest.json`, {
          cache: "no-cache",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const doc = (await res.json()) as LegalDoc;
        if (!cancelled) setState({ kind: "ready", doc });
      } catch (err) {
        if (!cancelled) {
          setState({
            kind: "error",
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, fallbackTitle]);

  return (
    <>
      <TopNav />
      <main className={styles.page}>
        <header className={styles.header}>
          <h1 className={styles.title}>
            {state.kind === "ready" ? state.doc.title : fallbackTitle}
          </h1>
          {state.kind === "ready" ? (
            <p className={styles.effective}>
              Effective {state.doc.effective} · version {state.doc.version}
            </p>
          ) : null}
        </header>
        {state.kind === "loading" ? (
          <p className={styles.muted}>Loading…</p>
        ) : null}
        {state.kind === "error" ? (
          <div className={styles.error}>
            <p>
              Couldn't load the current version: <code>{state.message}</code>
            </p>
            <p>
              You can read the source directly at{" "}
              <a href={`${LEGAL_BASE}/${kind}/latest.json`}>
                {LEGAL_BASE}/{kind}/latest.json
              </a>
              .
            </p>
          </div>
        ) : null}
        {state.kind === "ready" ? (
          <article className={styles.body}>
            <p className={styles.intro}>{state.doc.intro}</p>
            {state.doc.sections.map((s, i) => (
              <section key={i} className={styles.section}>
                <h2 className={styles.heading}>{s.heading}</h2>
                <p className={styles.paragraph}>{s.body}</p>
              </section>
            ))}
          </article>
        ) : null}
      </main>
      <Footer />
    </>
  );
}
